import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronIcon, FolderIcon } from '../../../icons';
import { useContextMenu, useItemEditing } from '../../../hooks';
import DeleteFoldersModal from '../../../../DocsArea/DeleteFoldersModal';
import { describeFolderDelete, getChildFolders } from '../../../../../utils/docOrganization';
import { FOLDER_DRAG_TYPE, DOC_DRAG_TYPE, folderDropZone } from '../constants';
import { sidebarTreeIndent } from '../../../utils/treeIndent';
import { useCopiedDoc } from '../../../../../hooks/useCopiedDoc';
import { pasteDocMenuLabel } from '../../../../../utils/docClipboard';
import { pasteCopiedDoc } from '../../../../../utils/pasteDoc';
import DocRow from './DocRow';

/* ── Nested folder row: expand/collapse, rename/delete, drop target, subfolders ── */
const FolderRow = ({
  folder,
  depth = 0,
  docsByFolder,
  allFolders,
  collapsedFolders,
  onToggleExpand,
  selectedDocId,
  onSelectDoc,
  onOpenInNewTab,
  onDeleteDoc,
  organization,
  dropTargetId,
  dropHint,
  draggedFolderId,
  onDragOverTarget,
  onDropOnTarget,
  onDragLeaveTarget,
  onFolderDragStart,
  onFolderDragEnd,
  onStartCreateChild,
  creatingUnderParentId,
  creatingFolder,
  newFolderName,
  onNewFolderNameChange,
  newFolderInputRef,
  onCommitNewFolder,
  onNewFolderKeyDown,
  createDoc,
}) => {
  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pasting, setPasting] = useState(false);
  const { renameFolder, deleteFolders } = organization;
  const copied = useCopiedDoc();

  const {
    isEditing,
    editValue,
    inputRef,
    startEditing,
    saveEdit,
    handleKeyDown,
    handleChange,
  } = useItemEditing(folder.name, (newName) => renameFolder(folder.id, newName));

  const expanded = !collapsedFolders[folder.id];
  const hintZone = dropHint?.folderId === folder.id ? dropHint.zone : null;
  const isDropTarget = hintZone === 'middle' || dropTargetId === `folder:${folder.id}`;
  const reorderPosition = hintZone === 'above' || hintZone === 'below' ? hintZone : null;
  const docs = docsByFolder.get(folder.id) || [];
  const childFolders = useMemo(
    () => getChildFolders(allFolders, folder.id),
    [allFolders, folder.id]
  );

  const deleteInfo = useMemo(
    () =>
      describeFolderDelete(allFolders, organization.docFolders, [folder.id]),
    [allFolders, organization.docFolders, folder.id]
  );

  const requestDelete = () => {
    closeContextMenu();
    if (deleteInfo.canSkipConfirm) {
      deleteFolders?.([folder.id], { cascadeSubfolders: true });
      return;
    }
    setShowDeleteModal(true);
  };

  const indentPx = sidebarTreeIndent(depth);
  const childIndentPx = sidebarTreeIndent(depth + 1);

  return (
    <div className="sidebar__docs-folder">
      <div
        role="button"
        tabIndex={0}
        className={`sidebar__item sidebar__docs-folder-row sidebar__item--has-children ${isDropTarget ? 'sidebar__docs-drop-target' : ''} ${draggedFolderId === folder.id ? 'sidebar__docs-folder-row--dragging' : ''}`}
        style={{ paddingLeft: indentPx }}
        onClick={() => !isEditing && onToggleExpand(folder.id)}
        onKeyDown={(e) => {
          if (isEditing) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand(folder.id);
          }
        }}
        onContextMenu={openContextMenu}
        draggable={!isEditing}
        onDragStart={(e) => {
          e.dataTransfer.setData(FOLDER_DRAG_TYPE, folder.id);
          e.dataTransfer.effectAllowed = 'move';
          onFolderDragStart?.(folder.id);
        }}
        onDragEnd={() => onFolderDragEnd?.()}
        onDragOver={(e) => {
          const isFolderDrag = e.dataTransfer.types.includes(FOLDER_DRAG_TYPE);
          const isDocDrag = e.dataTransfer.types.includes(DOC_DRAG_TYPE);
          if (!isFolderDrag && !isDocDrag) return;
          const zone = isFolderDrag && !isDocDrag ? folderDropZone(e) : 'middle';
          onDragOverTarget(e, folder.id, zone);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget)) return;
          onDragLeaveTarget(e);
        }}
        onDrop={(e) => {
          const isFolderDrag = Boolean(e.dataTransfer.getData(FOLDER_DRAG_TYPE));
          const zone = isFolderDrag ? folderDropZone(e) : 'middle';
          onDropOnTarget(e, folder.id, zone);
        }}
      >
        {reorderPosition === 'above' && (
          <div className="sidebar__reorder-indicator sidebar__reorder-indicator--above" />
        )}
        {reorderPosition === 'below' && (
          <div className="sidebar__reorder-indicator sidebar__reorder-indicator--below" />
        )}
        <div className="sidebar__item-row">
          <span className="sidebar__item-icon-container sidebar__docs-folder-icon" aria-hidden>
            <span className="sidebar__item-icon">
              <FolderIcon size={16} open={expanded} />
            </span>
          </span>
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              className="sidebar__item-input"
              value={editValue}
              onChange={handleChange}
              onBlur={saveEdit}
              onKeyDown={(e) => {
                e.stopPropagation();
                handleKeyDown(e);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="sidebar__item-label">{folder.name}</span>
          )}
          <span className="sidebar__docs-folder-count">{docs.length}</span>
          <span
            className="sidebar__item-chevron"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(folder.id);
            }}
          >
            <ChevronIcon size={10} expanded={expanded} />
          </span>
        </div>
      </div>

      {expanded && (
        <div className="sidebar__tree-children">
          {creatingFolder && creatingUnderParentId === folder.id && (
            <div
              className="sidebar__docs-new-folder"
              style={{ paddingLeft: childIndentPx }}
            >
              <FolderIcon size={16} />
              <input
                ref={newFolderInputRef}
                type="text"
                className="sidebar__item-input"
                value={newFolderName}
                placeholder="Folder name"
                onChange={(e) => onNewFolderNameChange(e.target.value)}
                onBlur={onCommitNewFolder}
                onKeyDown={onNewFolderKeyDown}
                onKeyUp={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {childFolders.map((child) => (
            <FolderRow
              key={child.id}
              folder={child}
              depth={depth + 1}
              docsByFolder={docsByFolder}
              allFolders={allFolders}
              collapsedFolders={collapsedFolders}
              onToggleExpand={onToggleExpand}
              selectedDocId={selectedDocId}
              onSelectDoc={onSelectDoc}
              onOpenInNewTab={onOpenInNewTab}
              onDeleteDoc={onDeleteDoc}
              organization={organization}
              dropTargetId={dropTargetId}
              dropHint={dropHint}
              draggedFolderId={draggedFolderId}
              onDragOverTarget={onDragOverTarget}
              onDropOnTarget={onDropOnTarget}
              onDragLeaveTarget={onDragLeaveTarget}
              onFolderDragStart={onFolderDragStart}
              onFolderDragEnd={onFolderDragEnd}
              onStartCreateChild={onStartCreateChild}
              creatingUnderParentId={creatingUnderParentId}
              creatingFolder={creatingFolder}
              newFolderName={newFolderName}
              onNewFolderNameChange={onNewFolderNameChange}
              newFolderInputRef={newFolderInputRef}
              onCommitNewFolder={onCommitNewFolder}
              onNewFolderKeyDown={onNewFolderKeyDown}
              createDoc={createDoc}
            />
          ))}

          {docs.length === 0 && childFolders.length === 0 && creatingUnderParentId !== folder.id ? (
            <p
              className="sidebar__docs-stub-text"
              style={{ paddingLeft: childIndentPx }}
            >
              Drag docs here
            </p>
          ) : (
            docs.map((doc) => (
              <DocRow
                key={doc.id}
                doc={doc}
                depth={depth + 1}
                selected={selectedDocId === doc.id}
                onSelect={onSelectDoc}
                onOpenInNewTab={onOpenInNewTab}
                onDeleteDoc={onDeleteDoc}
                organization={organization}
              />
            ))
          )}
        </div>
      )}

      {contextMenu &&
        createPortal(
          <div
            className="sidebar__context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              className="sidebar__context-menu-item"
              onClick={() => {
                closeContextMenu();
                startEditing();
              }}
            >
              Rename
            </button>
            <button
              className="sidebar__context-menu-item"
              onClick={(e) => {
                closeContextMenu();
                onStartCreateChild?.(e, folder.id);
              }}
            >
              New subfolder
            </button>
            {createDoc && (
              <button
                type="button"
                className="sidebar__context-menu-item"
                disabled={!copied?.docId || pasting || !organization?.ready}
                onClick={async () => {
                  if (!copied?.docId) return;
                  closeContextMenu();
                  setPasting(true);
                  try {
                    await pasteCopiedDoc({
                      createDoc,
                      moveDocToFolder: organization?.moveDocToFolder,
                      folderId: folder.id,
                    });
                  } finally {
                    setPasting(false);
                  }
                }}
              >
                {copied?.docId ? pasteDocMenuLabel(copied.title) : 'Paste doc here'}
              </button>
            )}
            <div className="sidebar__context-menu-divider" />
            <button
              className="sidebar__context-menu-item sidebar__context-menu-item--danger"
              onClick={requestDelete}
            >
              Delete folder
            </button>
          </div>,
          document.body
        )}

      <DeleteFoldersModal
        isOpen={showDeleteModal}
        info={deleteInfo}
        onConfirm={(cascade) => {
          setShowDeleteModal(false);
          deleteFolders?.([folder.id], { cascadeSubfolders: cascade });
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default FolderRow;
